import _ from 'lodash';
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    IconButton,
} from '@mui/material';
import {
    Business as BuildingIcon,
    OpenInNew as ExternalLinkIcon,
    Check as CheckmarkIcon,
    RemoveCircle as RemoveCircleIcon,
    Add as AddIcon,
    LocationOn as LocationIcon
} from '@mui/icons-material';

import SourceButton from '../PhoneTab/SourceButton.js';
import { formatExternalUrl } from '../../utils/formatUrl';
import Grid from '@mui/material/Grid2';
import { useState } from 'react';

const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
        case 'low':
            return { bg: '#10B981', text: '#ffffff' }; // Green background, white text
        case 'medium':
            return { bg: '#F59E0B', text: '#ffffff' }; // Orange background, white text
        case 'high':
            return { bg: '#EF4444', text: '#ffffff' }; // Red background, white text
        default:
            return { bg: '#6B7280', text: '#ffffff' }; // Gray background, white text
    }
};

const StatusItem = ({ type, text, textStyle }) => {
    const isWarning = type === 'warning';
    const isSuccess = type === 'success';
    const isNeutral = type === 'neutral';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            {isWarning && (
                <Box sx={{
                    backgroundColor: '#EF4444',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>!</Typography>
                </Box>
            )}
            {isSuccess && (
                <Box sx={{
                    backgroundColor: '#10B981',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <AddIcon sx={{ fontSize: 14, color: 'white', fontWeight: 'bold' }} />
                </Box>
            )}
            {isNeutral && (
                <RemoveCircleIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
            )}
            <Typography variant="body2" sx={{
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 400,
                ...textStyle
            }}>
                {text}
            </Typography>
        </Box>
    );
};

const formatCreationDate = (whoisData) => {
    const dateStr = whoisData?.registration_info?.created_date || whoisData?.creation_date || whoisData?.raw_whois_data?.creation_date;
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(date.getUTCDate()).padStart(2, '0');
        const yyyy = date.getUTCFullYear();
        return `${mm}/${dd}/${yyyy}`;
    } catch {
        return null;
    }
};

const toTitleCase = (str) =>
    str?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const STATE_TO_ABBR = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
    colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
    hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
    kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
    massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
    montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
    ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
    'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
    vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
    wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

const toStateCode = (state) => {
    if (!state) return null;
    if (state.length === 2) return state.toUpperCase();
    return STATE_TO_ABBR[state.toLowerCase()] || state;
};

const isRedacted = (val) =>
    !val || /redact|privacy|withheld|not\s*disclosed/i.test(val);

const cleanWhoisValue = (val) => (isRedacted(val) ? null : val);

const formatLocation = (data) => {
    const raw = data?.raw_whois_data;
    const registrant = data?.registration_info?.registrant;
    if (registrant) {
        const city = cleanWhoisValue(registrant.city || raw?.registrant_city);
        const state = cleanWhoisValue(registrant.state_code || registrant.state || raw?.registrant_state);
        const parts = _.filter([toTitleCase(city), toStateCode(state)], Boolean);
        if (parts.length > 0) return parts.join(', ');
    }
    const city = cleanWhoisValue(data?.registrant_city || raw?.registrant_city);
    const state = cleanWhoisValue(data?.registrant_state || raw?.registrant_state);
    const parts = _.filter([toTitleCase(city), toStateCode(state)], Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
};

const MainWebsiteCard = ({
    riskLevel = "Low",
    url,
    source = [],
    statusItems = [],
    domainTypes = [],
    whoisData = null
}) => {
    const riskColors = getRiskColor(riskLevel);
    const creationDate = formatCreationDate(whoisData);
    const location = formatLocation(whoisData);
    const uniqueStatusItems = statusItems.filter((item, index, self) =>
        index === self.findIndex((t) => t.text === item.text)
    );

    const isOfficialWebsite = domainTypes.includes('OWNS');
    const isInputWebsite = domainTypes.includes('INPUT_DATA');
    const title = isInputWebsite && !isOfficialWebsite ? 'Submitted website' : 'Official website';
    const backgroundColor = isOfficialWebsite ? '#E0E2F1' : '#dfeef5';

    const handleExternalLinkClick = (e) => {
        e.stopPropagation();
        if (url) {
            const formattedUrl = formatExternalUrl(url);
            window.open(formattedUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleCardClick = (e) => {
        if (url) {
            const formattedUrl = formatExternalUrl(url);
            window.open(formattedUrl, '_blank', 'noopener,noreferrer');
        }
    };

    if (!url) {
        return <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                mb: 2,
                backgroundColor: '#ffebee',
                borderRadius: '4px'
            }}
        >
            <Typography variant="body1" color="#b71c1c">No official website found</Typography>
        </Box>
    }
    return (
        <Paper
            onClick={handleCardClick}
            sx={{
                p: 3,
                borderRadius: 4,
                backgroundColor: backgroundColor, // Light gray background to match reference
                position: 'relative',
                width: '100%',
                minHeight: 160,
                maxWidth: '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'none',
                cursor: url ? 'pointer' : 'default',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': url ? {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                } : {}
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        backgroundColor: '#1E293B',
                        borderRadius: '50%',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <BuildingIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: '#1E293B',
                                fontSize: '1.125rem'
                            }}>
                                {title}
                            </Typography>
                            {isInputWebsite && (
                                <Chip
                                    label="Input Data"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            )}
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#4B5563',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: 400,
                                lineHeight: 1.4,
                                letterSpacing: '0.01em'
                            }}
                        >
                            {url || 'No URL available'}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
                    {creationDate && (
                        <Chip
                            label={`Created ${creationDate}`}
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                color: '#374151',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 1 }
                            }}
                        />
                    )}
                    {location && (
                        <Chip
                            icon={<LocationIcon sx={{ fontSize: 14 }} />}
                            label={location}
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                color: '#374151',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                '& .MuiChip-label': { px: 0.75 },
                                '& .MuiChip-icon': { color: '#6B7280', ml: 0.5, mr: -0.5 }
                            }}
                        />
                    )}
                    <IconButton
                        onClick={handleExternalLinkClick}
                        sx={{
                            backgroundColor: 'white',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            width: 40,
                            height: 40,
                            '&:hover': {
                                backgroundColor: '#F8FAFC',
                            }
                        }}
                        size="medium"
                    >
                        <ExternalLinkIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Risk Badge */}
            {riskLevel && <Chip
                icon={<CheckmarkIcon sx={{ fontSize: 14, color: 'white' }} />}
                label={`${riskLevel} risk`}
                size="small"
                sx={{
                    backgroundColor: riskColors.bg,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    mb: 2,
                    width: 'fit-content',
                    '& .MuiChip-label': {
                        px: 0.75
                    },
                    '& .MuiChip-icon': {
                        color: 'white',
                        ml: 0.75,
                        mr: 0.25
                    }
                }}
            />}

            {/* Status Items */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                maxHeight: '240px',
                overflowY: 'scroll',
            }}>
                {statusItems
                    .sort((a, b) => {
                        // Sort by type: warning first, then success, then neutral
                        if (a.type === 'warning' && b.type !== 'warning') return -1;
                        if (a.type !== 'warning' && b.type === 'warning') return 1;
                        if (a.type === 'success' && b.type === 'neutral') return -1;
                        if (a.type === 'neutral' && b.type === 'success') return 1;
                        return 0;
                    })
                    .filter((item, index, self) =>
                        index === self.findIndex((t) => t.text === item.text)
                    )
                    .map((item, index) => (
                        <StatusItem
                            key={index}
                            type={item.text === 'Entity website was found for the business and is valid' ? 'success' : item.type}
                            text={item.text}
                        />
                    ))}
            </Box>
            {/* Source Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                mt: 0
            }}>
                {uniqueStatusItems.length > 5 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', ml: 8, mt: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6B7280',
                                textAlign: 'center',
                                mt: 1,
                                fontSize: '0.800rem',
                            }}
                        >
                            {uniqueStatusItems.length} items ↓
                        </Typography>
                    </Box>
                )}
                <SourceButton sources={source} />
            </Box>
        </Paper>
    );
};

export default MainWebsiteCard;